import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Alert, Spinner, Badge, Form, InputGroup, Modal, Row, Col } from 'react-bootstrap';
import { API, Auth } from 'aws-amplify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faSync, faUserShield, faGift, faBuilding, faToggleOn, faToggleOff,
  faCalendarAlt, faEnvelope, faExclamationTriangle, faCheckCircle, faTimesCircle,
  faInfoCircle, faArrowLeft, faClock, faUsers
} from '@fortawesome/free-solid-svg-icons';
import * as queries from '../../graphql/queries';
import * as mutations from '../../graphql/mutations';

const BILLING_BYPASS_LICENSES = 9999;
const MAX_RETRY_ATTEMPTS = 3;

const DURATION_OPTIONS = [
  { value: 3, label: '3 Months' },
  { value: 6, label: '6 Months' },
  { value: 12, label: '1 Year' },
  { value: 18, label: '18 Months' },
  { value: 24, label: '2 Years' },
  { value: 36, label: '3 Years' },
];

// ========================
// HELPER: Re-fetch entity for latest _version to avoid conflicts
// ========================
const fetchLatestOrg = async (orgId) => {
  const response = await API.graphql({
    query: queries.getOrganization,
    variables: { id: orgId }
  });
  return response.data.getOrganization;
};

const fetchLatestUser = async (userId) => {
  const response = await API.graphql({
    query: queries.getUser,
    variables: { id: userId }
  });
  return response.data.getUser;
};

// ========================
// HELPER: Retry mutation with automatic _version refresh on conflict
// ========================
const retryMutation = async (mutationQuery, buildInput, entityFetcher, entityId, maxRetries = MAX_RETRY_ATTEMPTS) => {
  let lastError = null;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Always fetch latest version before each attempt
      const latestEntity = await entityFetcher(entityId);
      if (!latestEntity || latestEntity._deleted) {
        throw new Error('Entity not found or has been deleted');
      }
      const input = buildInput(latestEntity);
      const result = await API.graphql({
        query: mutationQuery,
        variables: { input }
      });
      return result;
    } catch (err) {
      lastError = err;
      const errorMessage = err?.errors?.[0]?.message || err?.message || '';
      const isConflict = errorMessage.includes('ConflictUnhandled') || 
                         errorMessage.includes('Conflict') ||
                         errorMessage.includes('The conditional request failed');
      
      if (isConflict && attempt < maxRetries) {
        console.warn(`Version conflict on attempt ${attempt}, retrying...`);
        // Small delay before retry
        await new Promise(resolve => setTimeout(resolve, 300 * attempt));
        continue;
      }
      throw err;
    }
  }
  throw lastError;
};

const BillingExemptionManagement = () => {
  // State for user search and listing
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [exemptUsers, setExemptUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // State for grant exemption modal
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [grantDuration, setGrantDuration] = useState(12);
  const [grantReason, setGrantReason] = useState('');
  const [granting, setGranting] = useState(false);

  // State for revoke confirmation modal
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [userToRevoke, setUserToRevoke] = useState(null);
  const [revoking, setRevoking] = useState(false);

  // State for per-org billing toggle view
  const [selectedExemptUser, setSelectedExemptUser] = useState(null);
  const [userOrganizations, setUserOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [togglingOrg, setTogglingOrg] = useState(null);

  // Fetch all exempt users on mount
  useEffect(() => {
    fetchExemptUsers();
  }, []);

  const fetchExemptUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      let allUsers = [];
      let nextToken = null;

      // First try filtering by billingExempt field
      try {
        do {
          const response = await API.graphql({
            query: queries.listUsers,
            variables: {
              filter: { billingExempt: { eq: true } },
              limit: 100,
              nextToken
            }
          });

          const users = response.data.listUsers.items.filter(u => !u._deleted);
          allUsers = [...allUsers, ...users];
          nextToken = response.data.listUsers.nextToken;
        } while (nextToken);
      } catch (filterErr) {
        console.warn('billingExempt filter failed, falling back to full scan:', filterErr);
        // Fallback: fetch all users and filter client-side
        allUsers = [];
        nextToken = null;
        do {
          const response = await API.graphql({
            query: queries.listUsers,
            variables: { limit: 200, nextToken }
          });
          const users = response.data.listUsers.items.filter(u => !u._deleted && u.billingExempt === true);
          allUsers = [...allUsers, ...users];
          nextToken = response.data.listUsers.nextToken;
        } while (nextToken);
      }

      setExemptUsers(allUsers);
    } catch (err) {
      console.error('Error fetching exempt users:', err);
      setError('Failed to load exempt users: ' + (err?.errors?.[0]?.message || err?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchExemptUsers();
    setRefreshing(false);
  };

  const searchUsers = async () => {
    if (!searchTerm.trim()) return;

    setSearching(true);
    setError(null);
    try {
      // Fetch all users and filter client-side because DynamoDB contains filter
      // is case-sensitive and the 'or' filter can be unreliable with AppSync
      let allUsers = [];
      let nextToken = null;

      do {
        const response = await API.graphql({
          query: queries.listUsers,
          variables: {
            limit: 200,
            nextToken
          }
        });

        const users = response.data.listUsers.items.filter(u => !u._deleted);
        allUsers = [...allUsers, ...users];
        nextToken = response.data.listUsers.nextToken;
      } while (nextToken);

      // Client-side case-insensitive search across email, firstName, lastName
      const term = searchTerm.trim().toLowerCase();
      const filtered = allUsers.filter(user => 
        (user.email || '').toLowerCase().includes(term) ||
        (user.firstName || '').toLowerCase().includes(term) ||
        (user.lastName || '').toLowerCase().includes(term)
      );

      setSearchResults(filtered.slice(0, 30)); // Limit to 30 results
    } catch (err) {
      console.error('Error searching users:', err);
      setError('Failed to search users: ' + (err.errors?.[0]?.message || err.message || 'Unknown error'));
    } finally {
      setSearching(false);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchUsers();
    }
  };

  // ========================
  // Grant Exemption — ALSO auto-enables bypass on all owned orgs
  // ========================
  const handleGrantClick = (user) => {
    setSelectedUser(user);
    setGrantDuration(12);
    setGrantReason('');
    setShowGrantModal(true);
  };

  const confirmGrantExemption = async () => {
    if (!selectedUser || !grantReason.trim()) {
      setError('Please provide a reason for granting the exemption');
      return;
    }

    setGranting(true);
    setError(null);

    try {
      const currentAdmin = await Auth.currentAuthenticatedUser();
      const adminEmail = currentAdmin.attributes.email;

      // Calculate expiry date
      const expiryDate = new Date();
      expiryDate.setMonth(expiryDate.getMonth() + grantDuration);
      const expiryISO = expiryDate.toISOString();

      // Step 1: Update user record with exemption (with retry for version conflicts)
      await retryMutation(
        mutations.updateUser,
        (latestUser) => ({
          id: latestUser.id,
          billingExempt: true,
          billingExemptUntil: expiryISO,
          billingExemptGrantedBy: adminEmail,
          billingExemptDuration: grantDuration,
          billingExemptReason: grantReason.trim(),
          _version: latestUser._version
        }),
        fetchLatestUser,
        selectedUser.id
      );

      console.log('✅ User billing exemption granted:', {
        userId: selectedUser.id,
        userEmail: selectedUser.email,
        duration: grantDuration,
        expiryDate: expiryISO,
        grantedBy: adminEmail
      });

      // Step 2: Auto-enable billing bypass on ALL owned organizations
      let orgsUpdated = 0;
      let orgsFailed = 0;
      try {
        let allOrgs = [];
        let nextToken = null;
        do {
          const response = await API.graphql({
            query: queries.listOrganizations,
            variables: {
              filter: { owner: { eq: selectedUser.cognitoID } },
              limit: 100,
              nextToken
            }
          });
          const orgs = response.data.listOrganizations.items.filter(o => !o._deleted);
          allOrgs = [...allOrgs, ...orgs];
          nextToken = response.data.listOrganizations.nextToken;
        } while (nextToken);

        for (const org of allOrgs) {
          try {
            await retryMutation(
              mutations.updateOrganization,
              (latestOrg) => ({
                id: latestOrg.id,
                billingBypass: true,
                billingBypassExpiry: expiryISO,
                billingBypassGrantedBy: adminEmail,
                billingBypassReason: `Billing exemption for user: ${selectedUser.email}`,
                preBillingBypassLicenses: latestOrg.purchasedLicenses || 0,
                purchasedLicenses: BILLING_BYPASS_LICENSES,
                _version: latestOrg._version
              }),
              fetchLatestOrg,
              org.id
            );
            orgsUpdated++;
            console.log(`✅ Billing bypass enabled for org: ${org.name} (${org.id})`);
          } catch (orgErr) {
            orgsFailed++;
            console.error(`❌ Failed to enable bypass for org: ${org.name}`, orgErr);
          }
        }
      } catch (orgsErr) {
        console.error('Error fetching user organizations:', orgsErr);
      }

      let message = `Billing exemption granted to "${selectedUser.email}" for ${grantDuration} months (until ${expiryDate.toLocaleDateString()}).`;
      if (orgsUpdated > 0) {
        message += ` Billing bypass auto-enabled on ${orgsUpdated} organization(s).`;
      }
      if (orgsFailed > 0) {
        message += ` Warning: ${orgsFailed} organization(s) failed to update — use the Orgs tab to enable manually.`;
      }

      setSuccessMessage(message);
      setShowGrantModal(false);
      setSelectedUser(null);
      setSearchResults([]);
      setSearchTerm('');
      await fetchExemptUsers();

      setTimeout(() => setSuccessMessage(null), 12000);
    } catch (err) {
      console.error('Error granting exemption:', err);
      const errMsg = err?.errors?.[0]?.message || err?.message || 'Unknown error';
      setError(`Failed to grant exemption: ${errMsg}`);
    } finally {
      setGranting(false);
    }
  };

  // ========================
  // Revoke Exemption
  // ========================
  const handleRevokeClick = (user) => {
    setUserToRevoke(user);
    setShowRevokeModal(true);
  };

  const confirmRevokeExemption = async () => {
    if (!userToRevoke) return;

    setRevoking(true);
    setError(null);

    try {
      // Step 1: Disable billing bypass on all organizations owned by this user FIRST
      let orgsReverted = 0;
      try {
        const orgsResponse = await API.graphql({
          query: queries.listOrganizations,
          variables: {
            filter: { owner: { eq: userToRevoke.cognitoID } },
            limit: 100
          }
        });

        const ownedOrgs = orgsResponse.data.listOrganizations.items.filter(o => !o._deleted);
        
        for (const org of ownedOrgs) {
          if (org.billingBypass === true || org.purchasedLicenses >= BILLING_BYPASS_LICENSES) {
            try {
              // Determine what licenses to restore
              const hasActiveSubscription = org.stripeSubscriptionId && org.subscriptionStatus === 'ACTIVE';
              const restoreLicenses = hasActiveSubscription 
                ? (org.preBillingBypassLicenses || org.purchasedLicenses || 0)
                : (org.preBillingBypassLicenses || 0);

              await retryMutation(
                mutations.updateOrganization,
                (latestOrg) => ({
                  id: latestOrg.id,
                  billingBypass: false,
                  billingBypassExpiry: null,
                  billingBypassGrantedBy: null,
                  billingBypassReason: null,
                  purchasedLicenses: restoreLicenses,
                  preBillingBypassLicenses: null,
                  _version: latestOrg._version
                }),
                fetchLatestOrg,
                org.id
              );
              orgsReverted++;
            } catch (orgErr) {
              console.error(`Failed to revert org: ${org.name}`, orgErr);
            }
          }
        }
      } catch (orgsErr) {
        console.error('Error reverting organizations:', orgsErr);
      }

      // Step 2: Update the user to remove exemption
      await retryMutation(
        mutations.updateUser,
        (latestUser) => ({
          id: latestUser.id,
          billingExempt: false,
          billingExemptUntil: null,
          billingExemptGrantedBy: null,
          billingExemptDuration: null,
          billingExemptReason: null,
          _version: latestUser._version
        }),
        fetchLatestUser,
        userToRevoke.id
      );

      console.log('✅ Billing exemption revoked:', {
        userId: userToRevoke.id,
        userEmail: userToRevoke.email,
        orgsReverted
      });

      setSuccessMessage(
        `Billing exemption revoked for "${userToRevoke.email}". ${orgsReverted} organization(s) reverted to normal billing.`
      );

      setShowRevokeModal(false);
      setUserToRevoke(null);
      
      // If we were viewing this user's orgs, go back
      if (selectedExemptUser?.id === userToRevoke.id) {
        setSelectedExemptUser(null);
        setUserOrganizations([]);
      }
      
      await fetchExemptUsers();
      setTimeout(() => setSuccessMessage(null), 8000);
    } catch (err) {
      console.error('Error revoking exemption:', err);
      const errMsg = err?.errors?.[0]?.message || err?.message || 'Unknown error';
      setError(`Failed to revoke exemption: ${errMsg}`);
    } finally {
      setRevoking(false);
    }
  };

  // ========================
  // Per-org billing toggle view
  // ========================
  const handleViewUserOrgs = async (user) => {
    setSelectedExemptUser(user);
    setLoadingOrgs(true);
    setError(null);

    try {
      let allOrgs = [];
      let nextToken = null;

      do {
        const response = await API.graphql({
          query: queries.listOrganizations,
          variables: {
            filter: { owner: { eq: user.cognitoID } },
            limit: 100,
            nextToken
          }
        });

        const orgs = response.data.listOrganizations.items.filter(o => !o._deleted);
        allOrgs = [...allOrgs, ...orgs];
        nextToken = response.data.listOrganizations.nextToken;
      } while (nextToken);

      // For each org, get the member count
      const orgsWithCounts = await Promise.all(
        allOrgs.map(async (org) => {
          try {
            const membersResponse = await API.graphql({
              query: queries.organizationMembersByOrganizationID,
              variables: { organizationID: org.id }
            });
            const memberCount = membersResponse.data.organizationMembersByOrganizationID.items
              .filter(m => !m._deleted).length;
            return { ...org, memberCount };
          } catch {
            return { ...org, memberCount: 0 };
          }
        })
      );

      setUserOrganizations(orgsWithCounts);
    } catch (err) {
      console.error('Error fetching user organizations:', err);
      setError('Failed to load user organizations: ' + (err?.errors?.[0]?.message || err?.message || 'Unknown error'));
    } finally {
      setLoadingOrgs(false);
    }
  };

  const handleBackToList = () => {
    setSelectedExemptUser(null);
    setUserOrganizations([]);
  };

  // ========================
  // Toggle billing bypass for a single org (with retry logic)
  // ========================
  const toggleBillingBypass = async (org, enable) => {
    setTogglingOrg(org.id);
    setError(null);

    try {
      const currentAdmin = await Auth.currentAuthenticatedUser();
      const adminEmail = currentAdmin.attributes.email;

      if (enable) {
        const bypassExpiry = selectedExemptUser?.billingExemptUntil;
        
        await retryMutation(
          mutations.updateOrganization,
          (latestOrg) => ({
            id: latestOrg.id,
            purchasedLicenses: BILLING_BYPASS_LICENSES,
            preBillingBypassLicenses: latestOrg.purchasedLicenses || 0,
            billingBypass: true,
            billingBypassExpiry: bypassExpiry,
            billingBypassGrantedBy: adminEmail,
            billingBypassReason: `Billing exemption for user: ${selectedExemptUser?.email || 'unknown'}`,
            _version: latestOrg._version
          }),
          fetchLatestOrg,
          org.id
        );

        console.log('✅ Billing bypass enabled:', { orgId: org.id, orgName: org.name });
      } else {
        // Disable billing bypass — restore original licenses
        await retryMutation(
          mutations.updateOrganization,
          (latestOrg) => {
            const hasActiveSubscription = latestOrg.stripeSubscriptionId && latestOrg.subscriptionStatus === 'ACTIVE';
            const restoreLicenses = hasActiveSubscription 
              ? (latestOrg.preBillingBypassLicenses || latestOrg.purchasedLicenses || 0)
              : (latestOrg.preBillingBypassLicenses || 0);

            return {
              id: latestOrg.id,
              billingBypass: false,
              billingBypassExpiry: null,
              billingBypassGrantedBy: null,
              billingBypassReason: null,
              purchasedLicenses: restoreLicenses,
              preBillingBypassLicenses: null,
              _version: latestOrg._version
            };
          },
          fetchLatestOrg,
          org.id
        );

        console.log('✅ Billing bypass disabled:', { orgId: org.id, orgName: org.name });
      }

      // Refresh org list
      await handleViewUserOrgs(selectedExemptUser);
      
      setSuccessMessage(
        `Billing bypass ${enable ? 'enabled' : 'disabled'} for "${org.name}".`
      );
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error toggling billing bypass:', err);
      const errMsg = err?.errors?.[0]?.message || err?.message || 'Unknown error';
      setError(`Failed to ${enable ? 'enable' : 'disable'} billing bypass for "${org.name}": ${errMsg}`);
    } finally {
      setTogglingOrg(null);
    }
  };

  // Enable/disable billing bypass for ALL orgs of the selected user
  const toggleAllOrgs = async (enable) => {
    setError(null);
    const orgsToToggle = userOrganizations.filter(org => {
      if (enable) return org.billingBypass !== true;
      return org.billingBypass === true;
    });

    if (orgsToToggle.length === 0) {
      setSuccessMessage(`All organizations are already ${enable ? 'exempt' : 'on normal billing'}.`);
      setTimeout(() => setSuccessMessage(null), 4000);
      return;
    }

    for (const org of orgsToToggle) {
      await toggleBillingBypass(org, enable);
    }
  };

  // ========================
  // Helper functions
  // ========================
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isExemptionExpired = (user) => {
    if (!user.billingExemptUntil) return true;
    return new Date(user.billingExemptUntil) < new Date();
  };

  const getDaysRemaining = (dateString) => {
    if (!dateString) return 0;
    const expiry = new Date(dateString);
    const now = new Date();
    const diff = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const getUserDisplayName = (user) => {
    const parts = [user.firstName, user.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : user.email;
  };

  const isBillingBypassActive = (org) => {
    if (org.billingBypass !== true) return false;
    const expiry = org.billingBypassExpiry ? new Date(org.billingBypassExpiry) : null;
    return expiry && expiry > new Date();
  };

  // ========================
  // RENDER: Per-Org Billing Toggle View
  // ========================
  if (selectedExemptUser) {
    return (
      <Card>
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">
              <FontAwesomeIcon icon={faBuilding} className="me-2" />
              Organizations — {getUserDisplayName(selectedExemptUser)}
            </h5>
            <small className="opacity-75">{selectedExemptUser.email}</small>
          </div>
          <Button variant="light" size="sm" onClick={handleBackToList}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back to Exempt Users
          </Button>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {successMessage && (
            <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          )}

          {/* User exemption info */}
          <Alert variant="info" className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <FontAwesomeIcon icon={faUserShield} className="me-2" />
                <strong>Exemption Status:</strong>{' '}
                {isExemptionExpired(selectedExemptUser) ? (
                  <Badge bg="danger">Expired</Badge>
                ) : (
                  <Badge bg="success">Active — {getDaysRemaining(selectedExemptUser.billingExemptUntil)} days remaining</Badge>
                )}
                <span className="ms-3">
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                  Expires: {formatDate(selectedExemptUser.billingExemptUntil)}
                </span>
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={() => toggleAllOrgs(true)}
                  disabled={isExemptionExpired(selectedExemptUser)}
                >
                  Enable All
                </Button>
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={() => toggleAllOrgs(false)}
                >
                  Disable All
                </Button>
              </div>
            </div>
          </Alert>

          {loadingOrgs ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Loading organizations...</p>
            </div>
          ) : userOrganizations.length === 0 ? (
            <Alert variant="warning">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              This user doesn't own any organizations.
            </Alert>
          ) : (
            <Table responsive hover>
              <thead className="table-light">
                <tr>
                  <th>Organization</th>
                  <th>Members</th>
                  <th>Current Billing</th>
                  <th>Bypass Status</th>
                  <th>Bypass Expiry</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {userOrganizations.map(org => {
                  const bypassActive = isBillingBypassActive(org);
                  const hasSubscription = org.stripeSubscriptionId && org.subscriptionStatus === 'ACTIVE';
                  
                  return (
                    <tr key={org.id}>
                      <td>
                        <div className="fw-bold">{org.name}</div>
                        <small className="text-muted">{org.contactEmail || 'No email'}</small>
                      </td>
                      <td>
                        <Badge bg="secondary">
                          <FontAwesomeIcon icon={faUsers} className="me-1" />
                          {org.memberCount}
                        </Badge>
                      </td>
                      <td>
                        {hasSubscription ? (
                          <Badge bg="success">
                            Active Subscription ({org.billingPeriod || 'Monthly'})
                          </Badge>
                        ) : org.purchasedLicenses > 0 && !bypassActive ? (
                          <Badge bg="info">{org.purchasedLicenses} Licenses (Granted)</Badge>
                        ) : bypassActive ? (
                          <Badge bg="info">Exempt (Free)</Badge>
                        ) : (
                          <Badge bg="warning">No Subscription</Badge>
                        )}
                      </td>
                      <td>
                        {bypassActive ? (
                          <Badge bg="success">
                            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                            Enabled
                          </Badge>
                        ) : org.billingBypass === true ? (
                          <Badge bg="danger">
                            <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                            Expired
                          </Badge>
                        ) : (
                          <Badge bg="secondary">
                            <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                            Disabled
                          </Badge>
                        )}
                      </td>
                      <td>
                        {org.billingBypassExpiry ? (
                          <span className={bypassActive ? 'text-success' : 'text-danger'}>
                            <FontAwesomeIcon icon={faClock} className="me-1" />
                            {formatDate(org.billingBypassExpiry)}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-center">
                        {togglingOrg === org.id ? (
                          <Spinner animation="border" size="sm" />
                        ) : bypassActive ? (
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => toggleBillingBypass(org, false)}
                            title="Disable billing bypass — org will need to pay"
                          >
                            <FontAwesomeIcon icon={faToggleOn} className="me-1" />
                            Disable
                          </Button>
                        ) : (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => toggleBillingBypass(org, true)}
                            disabled={isExemptionExpired(selectedExemptUser)}
                            title={isExemptionExpired(selectedExemptUser) 
                              ? "User's exemption has expired — renew first" 
                              : "Enable billing bypass — org will be free"}
                          >
                            <FontAwesomeIcon icon={faToggleOff} className="me-1" />
                            Enable
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    );
  }

  // ========================
  // RENDER: Main Exempt Users Panel
  // ========================
  return (
    <>
      <Card>
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faUserShield} className="me-2" />
            Billing Exemption Management
          </h5>
          <Button 
            variant="light" 
            size="sm" 
            onClick={handleRefresh} 
            disabled={refreshing}
          >
            <FontAwesomeIcon icon={faSync} spin={refreshing} className="me-1" />
            Refresh
          </Button>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {successMessage && (
            <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          )}

          {/* Search Section */}
          <Card className="mb-4">
            <Card.Header>
              <FontAwesomeIcon icon={faSearch} className="me-2" />
              Search Users to Grant Exemption
            </Card.Header>
            <Card.Body>
              <InputGroup className="mb-3">
                <Form.Control
                  placeholder="Search by email, first name, or last name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                />
                <Button 
                  variant="primary" 
                  onClick={searchUsers} 
                  disabled={searching || !searchTerm.trim()}
                >
                  {searching ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSearch} className="me-1" /> Search
                    </>
                  )}
                </Button>
              </InputGroup>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <Table responsive hover size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map(user => {
                      const isAlreadyExempt = user.billingExempt === true && !isExemptionExpired(user);
                      return (
                        <tr key={user.id}>
                          <td>{getUserDisplayName(user)}</td>
                          <td>
                            <FontAwesomeIcon icon={faEnvelope} className="me-1 text-muted" />
                            {user.email}
                          </td>
                          <td>
                            {isAlreadyExempt ? (
                              <Badge bg="success">
                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                Exempt until {formatDate(user.billingExemptUntil)}
                              </Badge>
                            ) : user.billingExempt === true ? (
                              <Badge bg="danger">Exemption Expired</Badge>
                            ) : (
                              <Badge bg="secondary">Standard Billing</Badge>
                            )}
                          </td>
                          <td className="text-center">
                            <Button
                              variant={isAlreadyExempt ? 'outline-info' : 'outline-success'}
                              size="sm"
                              onClick={() => handleGrantClick(user)}
                            >
                              <FontAwesomeIcon icon={faGift} className="me-1" />
                              {isAlreadyExempt ? 'Extend' : 'Grant Exemption'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {/* Exempt Users List */}
          <Card>
            <Card.Header>
              <FontAwesomeIcon icon={faUserShield} className="me-2" />
              Currently Exempt Users ({exemptUsers.length})
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2 text-muted">Loading exempt users...</p>
                </div>
              ) : exemptUsers.length === 0 ? (
                <Alert variant="info">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                  No users currently have billing exemptions. Use the search above to grant one.
                </Alert>
              ) : (
                <Table responsive hover>
                  <thead className="table-light">
                    <tr>
                      <th>User</th>
                      <th>Exemption Expires</th>
                      <th>Duration</th>
                      <th>Granted By</th>
                      <th>Reason</th>
                      <th>Status</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exemptUsers.map(user => {
                      const expired = isExemptionExpired(user);
                      const daysLeft = getDaysRemaining(user.billingExemptUntil);

                      return (
                        <tr key={user.id} className={expired ? 'table-danger' : ''}>
                          <td>
                            <div className="fw-bold">{getUserDisplayName(user)}</div>
                            <small className="text-muted">{user.email}</small>
                          </td>
                          <td>
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                            {formatDate(user.billingExemptUntil)}
                          </td>
                          <td>
                            {user.billingExemptDuration ? `${user.billingExemptDuration} months` : 'N/A'}
                          </td>
                          <td>
                            <small>{user.billingExemptGrantedBy || 'N/A'}</small>
                          </td>
                          <td>
                            <small className="text-muted" title={user.billingExemptReason}>
                              {user.billingExemptReason 
                                ? (user.billingExemptReason.length > 40 
                                    ? user.billingExemptReason.substring(0, 40) + '...' 
                                    : user.billingExemptReason) 
                                : 'N/A'}
                            </small>
                          </td>
                          <td>
                            {expired ? (
                              <Badge bg="danger">
                                <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                                Expired
                              </Badge>
                            ) : daysLeft <= 30 ? (
                              <Badge bg="warning">
                                <FontAwesomeIcon icon={faClock} className="me-1" />
                                {daysLeft} days left
                              </Badge>
                            ) : (
                              <Badge bg="success">
                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                Active ({daysLeft} days)
                              </Badge>
                            )}
                          </td>
                          <td className="text-center">
                            <div className="d-flex gap-1 justify-content-center">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleViewUserOrgs(user)}
                                title="View and toggle billing bypass per organization"
                              >
                                <FontAwesomeIcon icon={faBuilding} className="me-1" />
                                Orgs
                              </Button>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleGrantClick(user)}
                                title="Extend or renew exemption"
                              >
                                <FontAwesomeIcon icon={faGift} className="me-1" />
                                Extend
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleRevokeClick(user)}
                                title="Revoke billing exemption"
                              >
                                <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                                Revoke
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      {/* Grant Exemption Modal */}
      <Modal show={showGrantModal} onHide={() => setShowGrantModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faGift} className="me-2 text-success" />
            Grant Billing Exemption
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <Alert variant="info">
                <strong>User:</strong> {getUserDisplayName(selectedUser)}
                <br />
                <strong>Email:</strong> {selectedUser.email}
                {selectedUser.billingExempt && (
                  <>
                    <br />
                    <strong>Current Exemption:</strong>{' '}
                    {isExemptionExpired(selectedUser) 
                      ? 'Expired' 
                      : `Active until ${formatDate(selectedUser.billingExemptUntil)}`}
                  </>
                )}
              </Alert>

              <Form.Group className="mb-3">
                <Form.Label>
                  <strong>Duration</strong>
                </Form.Label>
                <Form.Select
                  value={grantDuration}
                  onChange={(e) => setGrantDuration(parseInt(e.target.value))}
                >
                  {DURATION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Exemption will expire on:{' '}
                  {(() => {
                    const d = new Date();
                    d.setMonth(d.getMonth() + grantDuration);
                    return d.toLocaleDateString();
                  })()}
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  <strong>Reason</strong> <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Why is this user being granted billing exemption? (required for audit trail)"
                  value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)}
                />
              </Form.Group>

              <Alert variant="info" className="mb-0">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                <small>
                  Granting the exemption will <strong>automatically enable billing bypass</strong> on all organizations 
                  owned by this user. You can fine-tune individual organizations afterwards using the <strong>Orgs</strong> button.
                </small>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGrantModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={confirmGrantExemption}
            disabled={granting || !grantReason.trim()}
          >
            {granting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Granting...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faGift} className="me-1" />
                Grant Exemption
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Revoke Confirmation Modal */}
      <Modal show={showRevokeModal} onHide={() => setShowRevokeModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
            Revoke Billing Exemption
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {userToRevoke && (
            <>
              <p>
                Are you sure you want to revoke the billing exemption for{' '}
                <strong>{getUserDisplayName(userToRevoke)}</strong> ({userToRevoke.email})?
              </p>
              <Alert variant="danger">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                <strong>This will:</strong>
                <ul className="mb-0 mt-1">
                  <li>Remove the billing exemption from this user</li>
                  <li>Disable billing bypass on ALL organizations they own</li>
                  <li>Restore original license counts (organizations without active Stripe subscriptions will revert to 0 licenses)</li>
                  <li>Organizations may be affected by trial expiration rules</li>
                </ul>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRevokeModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmRevokeExemption}
            disabled={revoking}
          >
            {revoking ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Revoking...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                Revoke Exemption
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BillingExemptionManagement;
